/**
 * What asks first (slice 3 §3.1, §8; Paper 51, 51b, 43c): the owner's page
 * of every app Crew offers, connected or not, at
 * `/settings/integrations/ask-first?app=gmail`. A list of apps on a desktop
 * (a picker on a phone), and the chosen app's writes by level, each with
 * Crew's default, a switch and, once changed, who changed it and Reset. The
 * catalog's default holds are on and locked until choice 2. Each app's card
 * links here ("What asks first (7)"); an MCP server's tools have theirs in
 * its tools window (`ToolAskFirst`).
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { BackLink } from "@crew/ui/components/back-link";
import { Button } from "@crew/ui/components/button";
import { Checkbox } from "@crew/ui/components/checkbox";
import { Link } from "@crew/ui/components/link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@crew/ui/components/select";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Lock } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorHint } from "../../app/api";
import {
  AskFirstOwner,
  actionsKey,
  appNote,
  linkText,
  type ActionRow,
  type ActionsPage,
  type AskApp,
} from "./ask-first-view";
import type { McpServer, McpTool } from "./integrations-api";
import { AppPanel } from "./ask-first-groups";

/** The page's apps and every write; the `integrations` event refetches them with the list. */
const useActions = (enabled = true) =>
  useQuery({
    queryKey: actionsKey,
    queryFn: () => api<ActionsPage>("GET", "/api/integrations/actions"),
    enabled,
  });

/** Ask first on or off, or Reset (`null`): shown at once, put back if it fails. */
function useSetAskFirst() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ask_first }: { action: string; ask_first: boolean | null }) => {
      const path = `/api/integrations/actions/${encodeURIComponent(action)}`;
      return ask_first === null
        ? api<{ action: ActionRow }>("DELETE", path)
        : api<{ action: ActionRow }>("PATCH", path, { ask_first });
    },
    onMutate: async ({ action, ask_first }) => {
      await client.cancelQueries({ queryKey: actionsKey });
      const before = client.getQueryData<ActionsPage>(actionsKey);
      if (before) {
        const next = (a: ActionRow) => ({ ...a, ask_first: ask_first ?? a.default_ask_first });
        client.setQueryData(actionsKey, {
          ...before,
          actions: before.actions.map((a) => (a.action === action ? next(a) : a)),
        });
      }
      return { before };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.before) client.setQueryData(actionsKey, ctx.before);
    },
    onSettled: () => client.invalidateQueries({ queryKey: actionsKey }),
  });
}

function useResetAll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ cleared: number }>("DELETE", "/api/integrations/actions"),
    onSettled: () => client.invalidateQueries({ queryKey: actionsKey }),
  });
}

// ── The link on an app's card ─────────────────────────────────────────────

/**
 * "What asks first (7)" for one app, to its page. Nothing for anyone but
 * the owner, or an app with no writes. `row`: the app card's foot on a
 * phone, a full-width line with a chevron.
 */
export function AskFirstLink(props: { app: string; row?: boolean; className?: string }) {
  return useContext(AskFirstOwner) ? <OwnerLink {...props} /> : null;
}

function OwnerLink({
  app,
  row = false,
  className,
}: {
  app: string;
  row?: boolean;
  className?: string;
}) {
  const actions = useActions();
  const rows = (actions.data?.actions ?? []).filter((r) => r.app === app);
  if (rows.length === 0) return null;
  return (
    <Link
      href={`/settings/integrations/ask-first?app=${encodeURIComponent(app)}`}
      data-ask-first-link={app}
      className={cn(
        row
          ? "flex w-full items-center gap-2.5 rounded-b-panel border-t border-border bg-foreground/[0.02] px-4 py-3.5 text-left text-sm font-medium text-foreground"
          : "flex w-fit shrink-0 items-center gap-1.5 text-xs font-medium text-foreground",
        className,
      )}
    >
      <Lock aria-hidden className={cn("shrink-0 text-copper", row ? "size-4" : "size-3")} />
      <span className={cn("flex-1", !row && "underline underline-offset-2")}>{linkText(rows)}</span>
      {row ? <ChevronRight aria-hidden className="size-4 shrink-0 text-hint" /> : null}
    </Link>
  );
}

// ── An MCP tool's, in its server's tools window ───────────────────────────

/**
 * Under a tool that's on and changes things (Paper 43c): "Ask first", ticked
 * when it does. The owner's only; it starts ticked (3b choice 8, as slice 3
 * amends it). Matched by the tool's name: the window never sees its slug.
 */
export function ToolAskFirst({ server, tool }: { server: McpServer; tool: McpTool }) {
  const owner = useContext(AskFirstOwner);
  const actions = useActions(owner && tool.enabled && !tool.read_only);
  const set = useSetAskFirst();
  const row = actions.data?.actions.find(
    (r) => r.app === `mcp:${server.name}` && r.title === tool.name,
  );
  // Just turned on, the list may be from before the switch saved: read it again, once.
  const missing = owner && tool.enabled && !tool.read_only && actions.isFetched && !row;
  const { refetch } = actions;
  useEffect(() => {
    if (missing) void refetch();
  }, [missing, refetch]);
  if (!owner || !row || !tool.enabled || tool.read_only) return null;
  return (
    <label
      data-tool-ask-first={tool.name}
      className="flex w-fit cursor-pointer items-center gap-2 pt-1.5"
    >
      <Checkbox
        checked={row.ask_first}
        disabled={set.isPending}
        onCheckedChange={(on) => set.mutate({ action: row.action, ask_first: on === true })}
        aria-label={`Ask first: ${tool.name}`}
      />
      <span className="text-meta font-medium text-copper">Ask first</span>
      <span className="text-meta text-hint">
        <span className="md:hidden">Shows you the change first.</span>
        <span className="hidden md:inline">
          Shows you the change and waits for you to approve it.
        </span>
      </span>
    </label>
  );
}

// ── The page ──────────────────────────────────────────────────────────────

/** The app in the URL (`?app=`), else the first connected one, else the first. */
function useChosenApp(apps: readonly AskApp[]): [AskApp | undefined, (app: string) => void] {
  const [params, setParams] = useSearchParams();
  const want = params.get("app");
  const chosen = apps.find((a) => a.app === want) ?? apps.find((a) => a.connected) ?? apps[0];
  const choose = (app: string) =>
    setParams(
      (p) => {
        p.set("app", app);
        return p;
      },
      { replace: true },
    );
  return [chosen, choose];
}

export function AskFirstPage({ timeZone }: { timeZone: string }) {
  const page = useActions();
  const set = useSetAskFirst();
  const data = page.data;
  const [chosen, choose] = useChosenApp(data?.apps ?? []);
  const changed = (data?.apps ?? []).reduce((n, a) => n + a.changed, 0);
  return (
    <div className="flex flex-col gap-5 pt-1" data-ask-first-page>
      <PageHead changed={changed} />
      {!data ? (
        <div className="flex justify-center py-16">
          {page.isError ? (
            <p className="text-body text-destructive">{errorHint(page.error)}</p>
          ) : (
            <Spinner />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-8">
          <AppList apps={data.apps} chosen={chosen?.app} onChoose={choose} />
          <AppPicker apps={data.apps} chosen={chosen?.app} onChoose={choose} />
          {chosen ? (
            <AppPanel
              key={chosen.app}
              app={chosen}
              rows={data.actions.filter((r) => r.app === chosen.app)}
              timeZone={timeZone}
              busy={set.isPending}
              error={set.error ? errorHint(set.error) : null}
              onSet={(action, ask_first) => set.mutate({ action, ask_first })}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function PageHead({ changed }: { changed: number }) {
  return (
    <>
      <BackLink href="/settings/integrations">Integrations</BackLink>
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-14">
        <div className="flex max-w-[640px] flex-1 flex-col gap-2.5">
          <h2 className="text-[26px] leading-[31px] font-semibold tracking-tight text-foreground md:text-[32px] md:leading-[38px]">
            Your crew shows you first
          </h2>
          <p className="text-body leading-[23px] text-copy">
            When it’s about to do something you’d want to see, it stops and sends you a card like
            this one. Approve it as it is, fix a word, or tell it what to change. Nothing happens
            until you do.
          </p>
          <p className="text-sm text-muted-foreground">
            Below, pick what it shows you first, app by app.
          </p>
          <ResetAll changed={changed} />
        </div>
        <SampleCard />
      </div>
    </>
  );
}

/** An approval card, as one would come (Paper board F): an example, not a real one. */
function SampleCard() {
  return (
    <figure
      aria-label="An example of the card your crew sends"
      className="flex w-full shrink-0 -rotate-1 flex-col gap-2.5 rounded-panel bg-card p-4 shadow-[0_0_0_1px_var(--color-border),0_12px_32px_rgb(31_42_68/0.08)] md:w-[440px] md:rotate-1 md:p-5"
    >
      <span className="text-xs font-medium tracking-label text-copper uppercase">
        Approve · Gmail
      </span>
      <span className="text-[17px] font-semibold text-foreground">
        Send this email to Maya Patel?
      </span>
      <span className="rounded-lg px-3.5 py-3 text-meta leading-[19px] text-copy shadow-[inset_0_0_0_1px_var(--color-border)]">
        <span className="font-medium text-foreground">The kitchen quote</span> · Hi Maya, the quote
        is $4,200 and we can start Thursday…
      </span>
      <span aria-hidden className="flex gap-2">
        <span className="rounded-md bg-foreground px-3.5 py-2 text-meta font-medium text-background">
          Approve and send
        </span>
        <span className="rounded-md px-3.5 py-2 text-meta font-medium text-foreground shadow-[inset_0_0_0_1px_var(--color-input)]">
          Change it
        </span>
      </span>
    </figure>
  );
}

/** Back to Crew's defaults, after a confirm: every app's, not the MCP servers' tools. */
function ResetAll({ changed }: { changed: number }) {
  const [open, setOpen] = useState(false);
  const reset = useResetAll();
  return (
    <>
      <Button
        variant="outline"
        className="w-fit bg-card"
        disabled={changed === 0}
        onClick={() => setOpen(true)}
        data-ask-first-reset-all
      >
        Back to Crew's defaults
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Back to Crew's defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              {changed === 1
                ? "The 1 action you changed goes"
                : `The ${changed} actions you changed go`}{" "}
              back to what Crew sets, in every app. Your MCP servers' tools keep theirs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reset.error ? (
            <p role="alert" className="text-meta text-destructive">
              {errorHint(reset.error)}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Keep them</AlertDialogCancel>
            <Button
              disabled={reset.isPending}
              data-ask-first-reset-all-confirm
              onClick={() => reset.mutate(undefined, { onSuccess: () => setOpen(false) })}
            >
              Back to defaults
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

type Chooser = { apps: AskApp[]; chosen: string | undefined; onChoose: (app: string) => void };

/** A desktop's list of apps, each with "1 changed" or "Defaults". */
function AppList({ apps, chosen, onChoose }: Chooser) {
  return (
    <nav aria-label="Apps" className="hidden w-55 shrink-0 flex-col gap-1 md:flex">
      {apps.map((a) => (
        <button
          key={a.app}
          type="button"
          data-ask-first-app={a.app}
          aria-current={a.app === chosen ? "true" : undefined}
          onClick={() => onChoose(a.app)}
          className={cn(
            "flex h-9.5 items-center gap-2 rounded-md px-2.5 text-left text-sm",
            a.app === chosen
              ? "bg-card font-medium text-foreground shadow-[0_0_0_1px_var(--color-border)]"
              : "text-copy hover:bg-accent",
          )}
        >
          <span className="flex-1 truncate">{a.name}</span>
          <span className={cn("text-xs", a.changed ? "text-copper" : "text-hint")}>
            {appNote(a)}
          </span>
        </button>
      ))}
      <p className="px-2.5 pt-4 text-meta leading-[19px] text-muted-foreground">
        Every app Crew offers is here, connected or not. Your MCP servers' tools ask first in their
        own tools window.
      </p>
    </nav>
  );
}

/** A phone's picker: the app, and how many changed. */
function AppPicker({ apps, chosen, onChoose }: Chooser) {
  return (
    <div className="md:hidden">
      <Select
        items={apps.map((a) => ({ value: a.app, label: a.name }))}
        value={chosen ?? null}
        onValueChange={(v) => typeof v === "string" && onChoose(v)}
      >
        <SelectTrigger aria-label="App" className="h-12 w-full bg-card text-body font-medium">
          <SelectValue />
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false} align="start">
          {apps.map((a) => (
            <SelectItem key={a.app} value={a.app} className="py-2 text-body">
              {a.name}
              <span className={cn("ml-auto pl-3 text-xs", a.changed ? "text-copper" : "text-hint")}>
                {appNote(a)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
